import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Modal } from '@ofself/shell';
import { DeskTable, ElementTable, ElementCell } from '../../components/nucleus/Shelf';
import { Plus, Lock } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { useToast } from '../../components/common';
import NucleusPage, { Verb, FindField } from '../../components/nucleus/NucleusPage';
import Segmented from '../../components/nucleus/Segmented';
import { Cell, Row } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import Inspector, { Band, Facts, Note, Tags, useOpen } from '../../components/nucleus/Inspector';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import SchemaBuilder from '../../components/SchemaBuilder';
import { similarSchemas, encryptionChange, EncryptionAck } from '../../components/schemaChecks';
import { tintOf } from '../nucleus/GraphModel';
import { SheetLabel } from '../nucleus/GraphSheets';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { JsonSheet } from './NodeSheets';
import { schemaApi, useSchemas, rightsOf } from './graphData';
import { NodeMark } from '../../components/nucleus/faces';
import { plural } from '../../nucleus/format';

// SCHEMAS — the kinds a node comes in, as a periodic table: each schema its
// name's symbol in its hue. Whose (all, yours, system) and category are the
// chip strip; a schema opens in the inspector with what it holds, what it
// extends and what extends it, its versions, and what you may do to it (by
// right: a system schema is everyone's and nobody edits it; a group's needs
// its admin). New schema belongs to the space you are in.

// whose: in a group, the group's own ("Book Club’s"), never "Yours"
const ownersOf = (group, spaceName) => [{ id: 'all', label: 'All' }, { id: 'mine', label: group ? `${spaceName}’s` : 'Yours' }, { id: 'system', label: 'System' }];

export default function SchemasPage() {
    const { groupId: group, spaceName, inSpace, section, page } = usePlace();
    const { user } = useAuth();
    const { groups = [] } = useGroup();
    const userId = user?.id || user?.user_id;
    const toast = useToast();
    const store = useSchemas(group);
    // in a group, making and changing schemas is an admin's (the server refuses a
    // member); a member reads them, and is told why the rest is off
    const access = useSpaceAccess();
    const locked = !!group && !access.isAdmin ? access.onlyAdmin('manage its schemas') : null;
    const [openId, open, close] = useOpen();
    const [find, setFind] = useState('');
    const [owner, setOwner] = useState('all');
    const [category, setCategory] = useState(null);
    const [sheet, setSheet] = useState(null);           // { kind: new|edit|build|extend|test|transfer, schema }
    const [loaded, setLoaded] = useState(false);
    useEffect(() => { let live = true; store.load().finally(() => { if (live) setLoaded(true); }); return () => { live = false; }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const schemas = store.schemas;
    const yours = (s) => (group ? s.groupId === group : !!userId && String(s.ownerId) === String(userId) && !s.groupId);
    const categories = useMemo(() => [...new Set(schemas.map((s) => s.category).filter(Boolean))].sort(), [schemas]);
    const shown = schemas.filter((s) => {
        if (category && s.category !== category) return false;
        if (owner === 'system' && !s.system) return false;
        if (owner === 'mine' && !yours(s)) return false;
        return true;
    });
    const q = find.trim().toLowerCase();
    const hit = (s) => !q || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.fields.some((f) => f.toLowerCase().includes(q));
    const byName = (name) => schemas.find((x) => x.name === name);

    const done = async (msg) => { toast.success?.(msg); await store.load(); };
    const actionsOf = (s) => {
        const r = rightsOf(s, { userId, groups });
        const noVersion = s.system ? 'A system schema: every app shares it, nobody edits it' : 'Only its owner makes its versions';
        const noManage = s.system ? 'A system schema belongs to no one' : s.groupId ? 'Only the group’s admin can do this' : 'Only its owner can do this';
        if (locked) {
            const off = { disabled: true, hint: locked, run: () => {} };
            return [
                { id: 'edit', label: 'Edit', ...off },
                { id: 'extend', label: 'Extend', ...off },
                { id: 'transfer', label: 'Transfer', ...off },
                { id: 'delete', label: 'Delete', danger: true, ...off },
            ];
        }
        return [
            r.version
                ? { id: 'edit', label: 'Edit', primary: true, run: () => setSheet({ kind: 'edit', schema: s }) }
                : { id: 'edit', label: 'Edit', disabled: true, hint: noVersion, run: () => {} },
            { id: 'extend', label: 'Extend', primary: !r.version, run: () => setSheet({ kind: 'extend', schema: s }) },
            { id: 'transfer', label: 'Transfer', disabled: !r.manage, hint: r.manage ? undefined : noManage, run: () => setSheet({ kind: 'transfer', schema: s }) },
            {
                id: 'delete', label: 'Delete', danger: true,
                // its owner may delete it whatever its scope: the builder makes every
                // schema public, so "private only" meant never
                disabled: !r.manage,
                hint: r.manage ? undefined : noManage,
                confirm: { title: `Delete ${s.name}?`, body: 'Every version of it goes, so no app can write it again. Nodes written with it keep their data. This can’t be undone.', label: 'Delete' },
                run: async () => { await schemaApi.remove(s); close(); await done(`Deleted ${s.name}.`); },
            },
        ];
    };

    const current = schemas.find((s) => String(s.id) === String(openId)) || null;
    // categories are many: one chip that opens them, lit while one is chosen
    const cats = categories.length > 0 && (
        <Menu
            trigger={(
                <button type="button" className={`nx-seg-menu${category ? ' is-active' : ''}`}>
                    {category || 'Category'}<span className="nx-seg__count">{category ? shown.length : categories.length}</span>
                </button>
            )}
            items={[
                { label: 'Every category', selected: !category, onSelect: () => setCategory(null) },
                { separator: true },
                ...categories.map((c) => ({ label: c, count: schemas.filter((x) => x.category === c).length, selected: category === c, onSelect: () => setCategory(c) })),
            ]}
        />
    );

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={loaded ? shown.length : undefined}
            verbs={<>
                <FindField value={find} onChange={setFind} placeholder="Find a schema" />
                <Verb primary icon={<Plus />} disabled={!!locked} title={locked || undefined} onClick={() => setSheet({ kind: 'new' })}>New schema</Verb>
            </>}
            inspector={current && (
                <SchemaInspector
                    key={current.id}
                    s={current}
                    store={store}
                    yours={yours(current)}
                    groups={groups}
                    actions={actionsOf(current)}
                    onOpenName={(name) => { const hitS = byName(name); if (hitS) open(hitS.id); }}
                    onTest={() => setSheet({ kind: 'test', schema: current })}
                    onClose={close}
                />
            )}
        >
            <div className="nx-filters">
                <Segmented label="Whose" options={ownersOf(group, spaceName)} value={owner} onChange={setOwner} />
                {cats}
            </div>
            {store.error && <Empty retry={() => store.load()}>{store.error}</Empty>}
            <DeskTable top={0} between={40}>
                <ElementTable heads={{ tag: 'Category' }}
                    title={owner === 'mine' ? `Made${inSpace}` : owner === 'system' ? 'System' : 'Every schema'}
                    count={loaded ? shown.length : undefined}
                    empty={loaded ? (owner === 'mine' ? (group ? `${spaceName} hasn’t made a schema yet.` : 'You haven’t made a schema here yet.') : 'No schemas here.') : undefined}
                >
                    {shown.map((s) => (
                        <Cell key={s.id} actions={actionsOf(s)}>
                            <ElementCell
                                hue={tintOf(s.name)}
                                tag={s.category || undefined}
                                badge={s.encrypted.length ? <Lock size={9} strokeWidth={2.2} aria-label={`encrypted: ${s.encrypted.join(', ')}`} /> : undefined}
                                mark={<NodeMark schema={s.name} schemaId={s.id} hue={tintOf(s.name)} size={50} />}
                                name={s.name}
                                note={[s.version > 1 ? `v${s.version}` : null, s.parent ? `extends ${s.parent}` : s.apps > 0 ? plural(s.apps, 'app') : plural(s.fields.length, 'field')].filter(Boolean).join(' · ')}
                                dim={!hit(s)}
                                current={String(s.id) === String(openId)}
                                onPress={() => open(s.id)}
                                title={s.description || s.name}
                            />
                        </Cell>
                    ))}
                </ElementTable>
            </DeskTable>

            {(sheet?.kind === 'new' || sheet?.kind === 'edit') && (
                <SchemaEditor
                    schema={sheet.kind === 'edit' ? sheet.schema : null}
                    all={schemas}
                    onClose={() => setSheet(null)}
                    onReuse={(raw) => { setSheet(null); open(raw.id); }}
                    onSave={async (body) => {
                        if (sheet.kind === 'edit') await schemaApi.version(sheet.schema, body);
                        else await schemaApi.create(group, body);
                        setSheet(null);
                        await done(sheet.kind === 'edit' ? `Saved a new version of ${sheet.schema.name}.` : `Made ${body.schema_name}.`);
                    }}
                />
            )}
            {sheet?.kind === 'extend' && (
                <BuilderSheet
                    title={`Extend ${sheet.schema.name}`}
                    mode="extend"
                    parent={sheet.schema.raw}
                    all={schemas}
                    onClose={() => setSheet(null)}
                    onSave={async (body) => { await schemaApi.create(group, body); setSheet(null); await done(`Made ${body.schema_name}, extending ${sheet.schema.name}.`); }}
                />
            )}
            {sheet?.kind === 'test' && <SchemaTester store={store} schema={sheet.schema} onClose={() => setSheet(null)} />}
            {sheet?.kind === 'transfer' && (
                <SchemaTransfer
                    schema={sheet.schema}
                    groups={groups}
                    onClose={() => setSheet(null)}
                    onHand={async (body, to) => { await schemaApi.transfer(sheet.schema, body); setSheet(null); await done(`${sheet.schema.name} is ${to} now.`); }}
                />
            )}
        </NucleusPage>
    );
}

function SchemaInspector({ s, store, yours, groups, actions, onOpenName, onTest, onClose }) {
    const [versions, setVersions] = useState(null);
    const [reading, setReading] = useState(null);       // { title, value }
    useEffect(() => {
        let live = true;
        store.versions(s).then((v) => { if (live) setVersions(v); });
        return () => { live = false; };
    }, [s.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const tint = tintOf(s.name);
    const group = s.groupId ? groups.find((g) => g.id === s.groupId) : null;
    const whose = s.system ? 'System' : s.groupId ? (group?.name || 'A group') : yours ? 'You' : 'Someone else';
    const props = s.raw?.json_schema?.properties || {};
    const parent = s.parent;
    return (
        <Inspector
            face={<NodeMark schema={s.name} schemaId={s.id} hue={tint} size={64} />}
            tag={['Schema', s.system ? 'system' : s.groupId ? 'a group’s' : yours ? 'yours' : null, s.category].filter(Boolean).join(' · ')}
            title={s.name}
            sub={s.description || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title="Where it stands">
                <Facts items={[
                    { label: 'Owner', value: whose },
                    s.version != null && { label: 'Version', value: `v${s.version}${versions && versions.length > 1 ? ` of ${versions.length}` : ''}` },
                    { label: 'Scope', value: s.scope },
                    { label: 'Apps writing it', value: s.apps || 'None' },
                    { label: 'Encrypted', value: s.encrypted.length ? plural(s.encrypted.length, 'field') : 'Nothing' },
                ]} />
            </Band>
            <Band title={plural(s.fields.length, 'field')} aside={<button type="button" className="nx-link" onClick={onTest}>Test a value</button>}>
                {s.fields.length === 0 && <Note>It declares no fields: anything goes.</Note>}
                <Tags items={s.fields.map((f) => ({
                    key: f,
                    label: `${f}${props[f]?.type ? ` · ${Array.isArray(props[f].type) ? props[f].type.join('/') : props[f].type}` : ''}`,
                    tone: s.encrypted.includes(f) ? 'warn' : undefined,
                }))} />
                {s.encrypted.length > 0 && <Note>{s.encrypted.join(', ')} {s.encrypted.length === 1 ? 'is' : 'are'} sealed with your key before it leaves your browser.</Note>}
            </Band>
            {(parent || s.children.length > 0) && (
                <Band title="Related">
                    {parent && <Row name={parent} sub="It extends this" onPress={() => onOpenName(parent)} />}
                    {s.children.map((c) => <Row key={c} name={c} sub="Extends it" onPress={() => onOpenName(c)} />)}
                </Band>
            )}
            {versions && versions.length > 1 && (
                <Band title="Versions" aside={versions.length}>
                    {versions.map((v, i) => (
                        <Row key={v.id} name={`v${v.version}`} sub={i === 0 ? 'The one in use' : undefined} onPress={() => setReading({ title: `${s.name} · v${v.version}`, value: JSON.parse(v.json || '{}') })} />
                    ))}
                </Band>
            )}
            <Band title="About" aside={<button type="button" className="nx-link" onClick={() => setReading({ title: `${s.name} · JSON schema`, value: s.raw?.json_schema || {} })}>JSON schema</button>}>
                <Note>{s.description || 'It says nothing more about itself.'}</Note>
            </Band>
            {reading && <JsonSheet title={reading.title} value={reading.value} onClose={() => setReading(null)} />}
        </Inspector>
    );
}

/** A schema as JSON — a new one, or the next version of one — with the builder a press away. */
function SchemaEditor({ schema, all, onSave, onClose, onReuse }) {
    const [name, setName] = useState(schema?.name || '');
    const [category, setCategory] = useState(schema?.category || '');
    const [encrypted, setEncrypted] = useState((schema?.encrypted || []).join(', '));
    const [json, setJson] = useState(schema?.json || '{\n  "type": "object",\n  "properties": {}\n}');
    const [build, setBuild] = useState(false);
    const [error, setError] = useState(null);
    const [working, setWorking] = useState(false);
    const [encAck, setEncAck] = useState(false);
    const encList = encrypted.split(',').map((x) => x.trim()).filter(Boolean);
    // a new schema: the ones already answering to the name, to use instead
    const similar = useMemo(() => (schema ? [] : similarSchemas(name, all.map((x) => x.raw).filter(Boolean))), [schema, name, all]);
    // a new version that changes what is encrypted: acknowledged before it saves
    const encChange = schema ? encryptionChange(schema.encrypted || [], encList) : { added: [], removed: [] };
    const encChanged = encChange.added.length > 0 || encChange.removed.length > 0;
    const go = async (body) => {
        setWorking(true);
        setError(null);
        try { await onSave(body); } catch (e) { setError(e?.message || 'It wasn’t saved.'); setWorking(false); }
    };
    const save = () => {
        let obj;
        try { obj = JSON.parse(json); } catch { setError('The JSON schema doesn’t parse.'); return; }
        const body = { schema_name: name.trim(), json_schema: obj, encrypted_fields: encList };
        if (category.trim()) body.category = category.trim();
        if (encChanged) body.posture_change = { acknowledge: true, migration: 'accept-mixed' };
        go(body);
    };
    if (build) {
        return (
            <BuilderSheet
                title={schema ? `New version of ${schema.name}` : 'New schema'}
                mode={schema ? 'update' : 'create'}
                existing={schema?.raw || null}
                all={all}
                onClose={() => setBuild(false)}
                onSave={onSave}
                onReuse={onReuse}
            />
        );
    }
    return (
        <Modal title={schema ? `New version of ${schema.name}` : 'New schema'} lead={schema ? 'Saving makes a new version; nodes already written keep theirs.' : 'Name it the way other apps would: bare and lowercase (belief, person).'} onClose={onClose}>
            <div className="graph-sheet">
                <SheetLabel>Name</SheetLabel>
                <input className="graph-input is-mono" aria-label="Name" placeholder="name" value={name} disabled={!!schema} onChange={(e) => setName(e.target.value)} />
                {similar.length > 0 && (
                    <div className="graph-sheet__lead" role="region" aria-label="Schemas that already exist">
                        Already exists: use one instead of making another?
                        {similar.map((x) => (
                            <button key={x.schema_name} type="button" className="graph-link" onClick={() => onReuse?.(x)}>Use {x.schema_name}</button>
                        ))}
                    </div>
                )}
                <SheetLabel>Category</SheetLabel>
                <input className="graph-input" aria-label="Category" placeholder="optional" value={category} onChange={(e) => setCategory(e.target.value)} />
                <SheetLabel>Encrypted fields</SheetLabel>
                <input className="graph-input is-mono" aria-label="Encrypted fields" placeholder="comma separated" value={encrypted} onChange={(e) => setEncrypted(e.target.value)} />
                <SheetLabel>JSON schema</SheetLabel>
                <textarea className="graph-input is-mono graph-code" rows={12} value={json} onChange={(e) => setJson(e.target.value)} aria-label="JSON schema" />
                <EncryptionAck change={encChange} checked={encAck} onChange={setEncAck} />
                {error && <p className="graph-sheet__error" role="alert">{error}</p>}
                <SheetFoot left={<FootButton onClick={() => setBuild(true)}>Use the builder</FootButton>}>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary disabled={!name.trim() || (encChanged && !encAck)} busy={working} onClick={save}>Save</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}

function BuilderSheet({ title, mode, parent = null, existing = null, all, onSave, onClose, onReuse = null }) {
    const [error, setError] = useState(null);
    return (
        <Modal title={title} onClose={onClose} className="graph-builder-modal" size="tall">
            <SchemaBuilder
                mode={mode}
                parentSchema={parent}
                existingSchema={existing}
                allSchemas={all.map((s) => s.raw)}
                onReuse={onReuse}
                onSave={async (payload) => { setError(null); try { await onSave(payload); } catch (e) { setError(e?.message || 'It wasn’t saved.'); } }}
                onCancel={onClose}
            />
            {error && <p className="graph-sheet__error" role="alert">{error}</p>}
        </Modal>
    );
}

function SchemaTester({ store, schema, onClose }) {
    const [sample, setSample] = useState('{}');
    const [result, setResult] = useState(null);
    return (
        <Modal title={`Test a value against ${schema.name}`} lead="Paste a node’s fields as JSON. Nothing is written." onClose={onClose}>
            <div className="graph-sheet">
                <textarea className="graph-input is-mono graph-code" rows={8} value={sample} onChange={(e) => setSample(e.target.value)} aria-label="Sample value" />
                {result && <p className={result === 'Valid' ? 'graph-sheet__ok' : 'graph-sheet__error'} role="status">{result}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Done</FootButton>
                    <FootButton primary onClick={async () => setResult(await store.validate(schema, sample))}>Validate</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}

function SchemaTransfer({ schema, groups, onHand, onClose }) {
    const [q, setQ] = useState('');
    const [found, setFound] = useState([]);
    const [error, setError] = useState(null);
    // the debounce stops a request being sent; `live` stops one already in flight
    // landing after a newer one and showing the older name's people
    useEffect(() => {
        if (!q.trim()) { setFound([]); return undefined; }
        let live = true;
        const t = setTimeout(() => api.searchUsers(q.trim(), 6)
            .then((r) => { if (live) setFound(r || []); })
            .catch(() => { if (live) setFound([]); }), 250);
        return () => { live = false; clearTimeout(t); };
    }, [q]);
    const hand = async (body, to) => {
        setError(null);
        try { await onHand(body, to); } catch (e) { setError(e?.message || 'It wasn’t handed on.'); }
    };
    const others = groups.filter((g) => g.id !== schema.groupId);
    return (
        <Modal title={`Hand ${schema.name} to…`} lead="Its new owner decides its versions. Nodes already written with it stay where they are." onClose={onClose}>
            <div className="graph-sheet">
                <SheetLabel>A group</SheetLabel>
                <Tags items={[
                    ...others.map((g) => ({ key: g.id, label: g.name, onPress: () => hand({ to_group_id: g.id }, `${g.name}’s`) })),
                    schema.groupId && { key: 'me', label: 'Back to me', onPress: () => hand({ to_personal: true }, 'yours') },
                ]} />
                {!others.length && !schema.groupId && <span className="graph-quiet-inline">You’re in no group.</span>}
                <SheetLabel>A person</SheetLabel>
                <input className="graph-input" placeholder="Username" aria-label="Username" value={q} onChange={(e) => setQ(e.target.value)} />
                {found.map((u) => {
                    const who = u.username || [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Someone';
                    return (
                        <button key={u.user_id || u.id} type="button" className="graph-link graph-found" onClick={() => hand({ to_user_id: u.user_id || u.id }, `${who}’s`)}>
                            {who}
                        </button>
                    );
                })}
                {error && <p className="graph-sheet__error" role="alert">{error}</p>}
                <SheetFoot><FootButton onClick={onClose}>Cancel</FootButton></SheetFoot>
            </div>
        </Modal>
    );
}
